import i18next, { t } from 'i18next'
import { List } from 'immutable'
import React from 'react'
import { none, some } from 'widgets-for-react'
import { visit_option } from '../../../../utils'
import { Webinar } from './webinars.types'
import { WebinarsOverviewState, UpdateFilter } from './webinars.widget'
import { decodeHTMLEntities } from '../../../../helpers'
import { getImageAttribute } from '../../utils/extra_images.mod'
import { div } from 'monadic_react'

type FilterItem = {
  kind: 'dynamic'
  id: number
  value: number
  name: string
}

type StaticFilterItem = {
  kind: 'static'
  id: string
  value: string
  name: string
}

const sortByNameAlphabetically = (a, b) => {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
}

export const WebinarsOverviewLayout = (props: {
  data: WebinarsOverviewState
  onPropChange: <K extends keyof WebinarsOverviewState>(key: K, value: WebinarsOverviewState[K]) => void
}) => {
  const { data, onPropChange } = props
  const {
    webinars: webinarsData,
    topicTaxonomy,
    countryTaxonomy,
    // pagination_page,
    // pagination_size,
    current_filters,
  } = data

  const isLoading = webinarsData.count() != 0 && webinarsData.toIndexedSeq().toArray().reduce((xs, x) => xs && x.kind == 'loaded' && x.value.items.length == 0, true)

  const webinars = webinarsData.toIndexedSeq()
    .toArray()
    .reduce((xs, x) => x.kind == 'loaded' ? xs.concat(x.value.items) : xs, Array<Webinar>())

  let staticCountries: Array<FilterItem | StaticFilterItem> = [
    { kind: 'static', id: 'nl', value: 'nl', name: 'Netherlands' },
    { kind: 'static', id: 'nl-be', value: 'nl-be', name: 'Belgium (Dutch)' },
  ]

  let countries: Array<FilterItem | StaticFilterItem> = []

  if (countryTaxonomy.kind == 'loaded') {
    countries = countries.concat(countryTaxonomy.value.map(tt => ({ kind: 'dynamic', id: tt.id, value: tt.id, name: tt.name })))
    if (window.wp.language !== 'nl') countries = countries.concat(staticCountries)
  }

  return <section className='block block--grid block--grid--image'>
    <div className="block__inner container container--m gutter--m spacing--m">
      <div className='block__filters'>
        <select id={i18next.t('webinar.subjects')}
          className="block__dropdown dropdown typo--s"
          value={visit_option<number, number>(current_filters.term)(v => v, () => 0)}
          onChange={e => onPropChange('current_filters', UpdateFilter(current_filters, 'term', Number(e.currentTarget.value) < 0 ? none() : some(Number(e.currentTarget.value))))}
        >
          <option value={0}>{i18next.t('webinar.subjects')}</option>
          {topicTaxonomy.kind == 'loaded' && topicTaxonomy.value.sort(sortByNameAlphabetically).map(t => <option key={t.id} value={t.id}>{decodeHTMLEntities(t.name)}</option>)}
        </select>

        <select id={i18next.t('webinar.countries')}
          className="block__dropdown dropdown typo--s"
          value={visit_option<number | string, number | string>(current_filters.country)(v => v, () => 0)}
          onChange={e => onPropChange('current_filters', UpdateFilter(current_filters, 'country', some(e.currentTarget.value)))}
        >
          <option value={0}>{i18next.t('webinar.countries')}</option>
          {countries.sort(sortByNameAlphabetically).map(tt => ({ id: tt.id, value: tt.id, name: tt.name })).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}

        </select>
      </div>
      <div className="block__content">
        <ul className="block__grid grid ls-columns">
          {
            current_filters.country.v.kind == 'r' && (current_filters.country.v.v == 'nl' || current_filters.country.v.v == 'nl-be')
              ? <li><p>Are you looking for the Dutch/Belgian webinars? <a href='/nl/webinars/'  target="_blank">Click here</a></p></li>
              : List(webinars)
                // .skip(pagination_size * (pagination_page - 1))
                // .take(pagination_size)
                .map(entry => {
                  let header = entry.custom_blocks ? entry.custom_blocks.filter(f => f.blockName === 'vidda/header')[0]?.attrs : null

                  const title = header && header.title ? header.title : entry.title
                  const intro = header && header.intro ? header.intro  : undefined

                  return <li className="grid__item grid__item--image ls-item" key={entry.id}>
                    {/* TODO: set correct slug  */}
                    <a className="grid__link grid__inner ls-reversed border-radius--s" href={entry.slug} >
                      <div className="grid__content ls-bottom-item">
                        {title && <h3 className="grid__title typo--ml ff--primary ls-second-item">{decodeHTMLEntities(title)}</h3>}
                        {intro ? <p className="grid__intro">{decodeHTMLEntities(intro)}</p> : <div className="grid__intro"></div>}
                        <div className="grid__fauxlink fauxlink">
                          <div className="fauxlink__inner">
                            <span className="fauxlink__copy">{i18next.t('webinar.watch_this')}</span>
                            <svg className="fauxlink__icon" height="14" width="16" aria-hidden="true"><use xlinkHref="#arrow" /></svg>
                          </div>
                        </div>
                      </div>
                      {header.image ?
                        <div className="grid__visual">
                          <img
                            className="grid__image lazyload"
                            src='/frontend/images/empty.png'
                            width={getImageAttribute(header.image, 'grid-s', 'width')}
                            height={getImageAttribute(header.image, 'grid-s', 'height')}
                            alt={header.image.alt_text ? header.image.alt_text : ''}
                            data-sizes="auto"
                            data-src={getImageAttribute(header.image, 'grid-s', 'source_url')}
                            data-srcset={`${getImageAttribute(header.image, 'grid-s', 'source_url')} 440w,
                        ${getImageAttribute(header.image, 'grid-m', 'source_url')} 880w,
                        ${getImageAttribute(header.image, 'grid-l', 'source_url')} 1540w`}
                          />
                        </div>
                        :
                        <div className="grid__placeholder"></div>
                      }
                    </a>
                  </li>
                })
          }
          {isLoading && <h3 className="block__title ff--primary typo--ml">{i18next.t('webinar.no_results')}</h3>}
        </ul>
      </div>
    </div>
  </section>
}